import React, {
    useEffect,
    useRef,
    useState,
    forwardRef,
    useImperativeHandle,
  } from "react";
  import Chart from "chart.js/auto";
  import { useNavigate } from "react-router-dom";
  import {
    getEmissionPreEventActivity,
    getEmissionDuringEventActivity,
    getEmissionPostEventActivity,
  } from "./apiService";
  import "./EmissionDoughnutChart.css";
  
  const EmissionDoughnutChart = forwardRef(
    ({ phase, eventId, totalEventEmission, event, onSliceClick = null }, ref) => {
      const chartRef = useRef(null);
      const canvasRef = useRef(null);
      const [mainData, setMainData] = useState([]);
      const [detailedData, setDetailedData] = useState([]); // only used for “during”
      const [isDataFetched, setIsDataFetched] = useState(false);
      const [hasPositiveData, setHasPositiveData] = useState(false);
      const [totalEmission, setTotalEmission] = useState(0);
      const navigate = useNavigate();
  
      // Expose toBase64Image() if parent wants to grab the image
      useImperativeHandle(ref, () => ({
        toBase64Image: () => {
          if (chartRef.current) {
            return chartRef.current.toBase64Image();
          }
          return null;
        },
      }));
  
      // Choose which API to call based on “phase”
      useEffect(() => {
        if (!eventId) return;
  
        const fetchData = async () => {
          try {
            let response;
            switch (phase) {
              case "pre":
                response = await getEmissionPreEventActivity(eventId);
                setMainData(response.mainData);
                setIsDataFetched(true);
                // during and post do not have detailedData
                break;
  
              case "during":
                response = await getEmissionDuringEventActivity(eventId);
                setMainData(response.mainData);
                setDetailedData(response.detailedData);
                setIsDataFetched(true);
                break;
  
              case "post":
                response = await getEmissionPostEventActivity(eventId);
                setMainData(response.mainData);
                setIsDataFetched(true);
                break;
  
              default:
                console.error("Unknown phase:", phase);
                return;
            }
  
            // Once we have `mainData`, check for any positive values & compute totalEmission
            const values = response.mainData.map((item) =>
              parseFloat(item.cumulativevalue || 0)
            );
            const hasPositive = values.some((v) => v > 0);
            setHasPositiveData(hasPositive);
  
            const sum = values.reduce((acc, v) => acc + v, 0);
            setTotalEmission(sum);
          } catch (error) {
            console.error(`Error fetching ${phase} data:`, error);
            setIsDataFetched(true);
            setHasPositiveData(false);
          }
        };
  
        fetchData();
      }, [phase, eventId]);
  
      // Once we have mainData (and detailedData if “during”), build the Chart.js instance
      useEffect(() => {
        if (!hasPositiveData || mainData.length === 0) return;
  
        // 1) Labels come from mainData[].emissionType (with slight rename for “Shuttle Service” in “during”)
        const labels = mainData.map((item) =>
          phase === "during" && item.emissionType === "Shuttle Service"
            ? "Shuttle/Local Transport"
            : phase === "post"
            ? item.emissionType.replace("Quantity of ", "").trim()
            : item.emissionType
        );
  
        // 2) Data values are cumulativevalue numbers
        const dataValues = mainData.map((item) =>
          parseFloat(item.cumulativevalue || 0)
        );
  
        const chartData = {
          labels,
          datasets: [
            {
              label: "Carbon Emission",
              data: dataValues,
              backgroundColor: phase === "during"
                ? [
                    "#a18cd1",
                    "#ff9a9e",
                    "#8fd3f4",
                    "#a1c4fd",
                    "#8de9d5",
                    "#f67280",
                    "#6c5ce7",
                  ]
                : ["#a18cd1", "#ff9a9e", "#8fd3f4", "#a1c4fd", "#8de9d5"],
              borderWidth: 3,
              borderRadius: 8,
              cutout: "85%",
              borderColor: "#e4e8f6",
              hoverBackgroundColor: phase === "during"
                ? [
                    "#a18cd1",
                    "#ff9a9e",
                    "#8fd3f4",
                    "#a1c4fd",
                    "#8de9d5",
                    "#f67280",
                    "#6c5ce7",
                  ]
                : ["#a18cd1", "#ff9a9e", "#8fd3f4", "#a1c4fd", "#8de9d5"],
              hoverBorderWidth: 3,
              hoverBorderColor: "#e4e8f6",
              hoverOffset: 6,
              spacing: 3,
            },
          ],
        };
  
        // 3) For “during”, prepare a second dataset for the click‐through details
        const detailedDatasets =
          phase === "during"
            ? detailedData.map((section) => ({
                labels: section.data.map((sub) => sub.emissionSubcategory),
                data: section.data.map((sub) => Number(Number(sub.value).toFixed(2))),
                backgroundColor: [
                  "#a18cd1",
                  "#ff9a9e",
                  "#8fd3f4",
                  "#a1c4fd",
                  "#8de9d5",
                  "#f67280",
                  "#6c5ce7",
                ],
                borderColor: [
                  "#a18cd1",
                  "#ff9a9e",
                  "#8fd3f4",
                  "#a1c4fd",
                  "#8de9d5",
                  "#f67280",
                  "#6c5ce7",
                ],
                borderWidth: 1,
              }))
            : [];
  
        const ctx = canvasRef.current.getContext("2d");
        if (chartRef.current) {
          chartRef.current.destroy();
        }
  
        chartRef.current = new Chart(ctx, {
          type: "doughnut",
          data: chartData,
          options: {
            onHover: (evt, elements) => {
              // if there is a slice under the pointer, show pointer cursor
              evt.native.target.style.cursor = elements.length ? "pointer" : "default";
            },
            plugins: {
              tooltip: {
                usePointStyle: true,
                callbacks: {
                  label: (context) => {
                    let label = context.label || "";
                    if (context.parsed) {
                      const val = context.parsed.toFixed(2);
                      label += `: ${val} KgCO₂`;
                    }
                    return label;
                  },
                },
                backgroundColor: "rgba(0, 0, 0, 0.7)",
                titleColor: "#ffffff",
                bodyColor: "#ffffff",
                borderColor: "rgba(75, 192, 192, 1)",
                borderWidth: 0,
              },
              legend: {
                display: true,
                position: "bottom",
                labels: {
                  generateLabels: (chart) => {
                    const ds = chart.data.datasets[0].data;
                    const lbls = chart.data.labels;
                    return ds.map((d, i) => ({
                      text: ` ${lbls[i]}: ${Number(d.toFixed(2)).toLocaleString()} KgCO₂ (${(
                        (d / totalEmission) *
                        100
                      ).toFixed(2)}%)`,
                      fillStyle: chart.data.datasets[0].backgroundColor[i],
                      index: i,
                    }));
                  },
                  usePointStyle: true,
                  pointStyle: "circle",
                  padding: 12,
                  font: { size: 10 },
                },
                align: "start",
              },
              datalabels: { display: false },
            },
          },
          plugins: [
            {
              id: "centerText",
              beforeDatasetsDraw: (chart) => {
                const { ctx } = chart;
                const x = chart.chartArea.left + (chart.chartArea.right - chart.chartArea.left) / 2;
                const y = chart.chartArea.top + (chart.chartArea.bottom - chart.chartArea.top) / 2;
  
                ctx.save();
                ctx.font = "12px Rubick, sans-serif";
                ctx.fillStyle = "#2e688dbb";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText("TOTAL", x, y - 20);
  
                ctx.font = "16px Rubik, Lado, sans-serif";
                ctx.fillStyle = "#2e688d";
                ctx.fillText(`${totalEmission.toLocaleString()} KgCO₂`, x, y);
  
                // show percentage of the whole event if totalEventEmission is given
                if (typeof totalEventEmission === "number" && totalEventEmission > 0) {
                  ctx.fillStyle = "#2e688dbb";
                  ctx.font = "14px Rubick, sans-serif";
                  const pct = ((totalEmission / totalEventEmission) * 100).toFixed(2);
                  ctx.fillText(`(${pct}%)`, x, y + 20);
                }
                ctx.restore();
              },
              afterEvent: (chart, args) => {
                // only “during” phase supports click‐through
                if (phase !== "during" || !onSliceClick) return;
  
                const event = args.event;
                if (event.type === "click") {
                  const elements = chart.getElementsAtEventForMode(
                    event,
                    "nearest",
                    { intersect: true },
                    false
                  );
                  if (elements.length) {
                    const idx = elements[0].index;
                    if (idx < detailedDatasets.length) {
                      const detail = detailedDatasets[idx];
                      onSliceClick({
                        name: chart.data.labels[idx],
                        label: detail.labels,
                        data: detail.data,
                      });
                    }
                  }
                }
              },
            },
          ],
        });
  
        return () => {
          if (chartRef.current) {
            chartRef.current.destroy();
          }
        };
      }, [mainData, hasPositiveData, totalEventEmission, phase, detailedData, onSliceClick]);
  
      // Header text by phase
      const headerText =
        phase === "pre"
          ? "Emissions of Pre-Event Activities"
          : phase === "during"
          ? "Emissions of During-Event Activities"
          : "Emissions of Post-Event Activities";
  
      // Where to navigate if there’s no data
      const missingDataHandler = () => {
        if (phase === "pre") {
          navigate("/events/pre-event-planning/pre-planning", { state: { event } });
        } else if (phase === "during") {
          navigate("/events/during-event-planning/attendee-details", { state: { event } });
        } else {
          navigate("/events/post-event-planning/waste-generation", { state: { event } });
        }
      };
  
      return (
        <div className="chart-container">
          <div className="chart-header">
            <h3>{headerText}</h3>
          </div>
          <div className="chart-data">
            {isDataFetched && hasPositiveData ? (
              <canvas ref={canvasRef} />
            ) : (
              <div style={{ textAlign: "center", padding: "2rem" }}>
                <button className="btn cta-button" onClick={missingDataHandler}>
                  {phase === "pre"
                    ? "Add data to Pre-Event Planning"
                    : phase === "during"
                    ? "Add data to During-Event Planning"
                    : "Add data to Post-Event Planning"}
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }
  );
  
  export default EmissionDoughnutChart;
  